// FOUNDER-WALK-LIVE-RECONCILE-025 · S2/S3 — THE MOUNTAIN SELECTOR'S PURE HALF.
//
// Two facts the R1a selector (`MountainSelectModal`) and its site-wide mount (`MountainSelectLayer`)
// share, kept free of React and of `KeylessMap` (which pulls maplibre-gl and a CSS import) so a unit
// test can assert them without a browser:
//
//   • `selectLayout(narrow)` — the two panes' styles. The selector used to be a fixed ROW: a list
//     floored at 260px beside a map floored at min(520px, 52vw). At 390 the overlay leaves 358px, the
//     floors alone want 260 + 203 = 463, and the dialog's `overflow: hidden` clipped the map to a
//     sliver — one reason the design's own header panel hid its map affordance on phones, and why the
//     founder saw two different selectors at the two widths. `split` (desktop) reproduces the
//     S37 · card 86bb6gmyw values byte-for-byte; `stacked` (phone) puts the list above the map.
//
//   • `locateFromDetail(detail)` — the mountain CODE a `gf:mountain-select` request may carry so the
//     selector pans its real map to that pin on open (the design header's per-row "Show on the map").
//     A non-string or empty value is "no locate", never a guess.

import type { CSSProperties } from "react";

export const SELECT_NARROW_QUERY = "(max-width: 767px)";
/** The list/map rule colour — the modal's own `RULE`, restated here so the layout is self-contained. */
const RULE = "#cbe0e4";

export interface SelectLayout {
  name: "split" | "stacked" | "list";
  direction: "row" | "column";
  list: CSSProperties;
  map: CSSProperties;
}

export function selectLayout(narrow: boolean, showMap = true): SelectLayout {
  // Kit 2.01: with the map pane hidden (toggled off, or below 768) the panel is the list alone, full width.
  if (!showMap) {
    return {
      name: "list",
      direction: "column",
      list: { flex: "1 1 auto", width: "100%", maxWidth: "100%", minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 },
      map: { display: "none" },
    };
  }
  if (!narrow) {
    return {
      name: "split",
      direction: "row",
      // S37 · card 86bb6gmyw — the menu column is sized to its text and the map takes the rest.
      list: { flex: "0 1 auto", width: "max-content", maxWidth: "min(360px, 42vw)", minWidth: 260, display: "flex", flexDirection: "column", minHeight: 0, borderRight: `1px solid ${RULE}` },
      map: { flex: "1 1 auto", width: "auto", minWidth: "min(520px, 52vw)", minHeight: 320, display: "flex", flexDirection: "column" },
    };
  }
  return {
    name: "stacked",
    direction: "column",
    // the list takes the full width and caps its own height so the map is on screen without scrolling
    // the dialog; its rows keep their own scroll container (unchanged in the modal: `overflowY: auto`).
    list: { flex: "0 1 auto", width: "100%", maxWidth: "100%", minWidth: 0, maxHeight: "42vh", display: "flex", flexDirection: "column", minHeight: 0, borderBottom: `1px solid ${RULE}` },
    // the map takes what is left of the 84vh dialog, and never less than a usable pane.
    map: { flex: "1 1 260px", width: "100%", minWidth: 0, minHeight: 260, display: "flex", flexDirection: "column" },
  };
}

/** The Kit 2.01 panel rules (SG BETA V2.01: mtnMapToggleLabel / mtnPanelWidth) as one pure function, so the shared
 *  fallback selector cannot drift from the design panel it mirrors. `mapWanted` is the visitor's toggle.
 *  SNOWCLUB-MOUNTAIN-PICKER-MAP-STAGING-20261025 (founder-approved): below 768 the shared selector is NO LONGER
 *  list-only — the same toggle is offered and the map shows in the existing `stacked` layout under the list.
 *  The design export's own inline panel is untouched (it stays list-only there). */
export function kitPanelState(narrow: boolean, mapWanted: boolean) {
  const showMap = mapWanted;
  return {
    showMap,
    toggleVisible: true,
    toggleLabel: showMap ? "Hide map" : "Show the map",
    panelWidth: narrow ? "100%" : showMap ? "min(880px, calc(100vw - 32px))" : "min(420px, calc(100vw - 32px))",
  };
}

/** Kit 2.01 lists a bookable mountain ONCE, in the Bookable group, and the country groups exclude it.
 *  `pinned` = the rows that belong in Bookable (in the caller's order); `rest` = everything else, which is
 *  what the country groups are built from. A mountain joins Bookable ONLY when the current served bookable
 *  set confirms it — never from a seed. While the feed is unavailable (null) or empty, Whistler included,
 *  every row stays in `rest` (its country group) exactly once. */
export function splitBookable<T extends { href: string }>(
  rows: T[],
  codeOf: (href: string) => string | null,
  servedBookable: Set<string> | null,
): { pinned: T[]; rest: T[] } {
  const pinned: T[] = [];
  const rest: T[] = [];
  for (const r of rows) {
    const c = codeOf(r.href);
    (c && servedBookable?.has(c) ? pinned : rest).push(r);
  }
  return { pinned, rest };
}

/** The request an entry point may carry. Everything is optional; an empty detail is a neutral open. */
export interface MountainSelectRequest {
  mode?: "marketplace" | "transport" | "directory" | null;
  locate?: string | null;
  source?: string;
}

/** MOUNTAIN-MAP-MODE-KIT201-20260925 — "Open the map" asks the DESIGN's own Kit 2.01 map mode first. */
export const MOUNTAIN_MAP_EVENT = "sg:mountain-map";
export interface MountainMapRequest {
  locate: string | null;
  /** set synchronously by the design's listener; still false afterwards means no design panel is mounted */
  handled: boolean;
}

/** Ask a mounted design panel to open in map mode (marking `locate`'s row). True = a design answered, so the
 *  caller must open NOTHING else; false = no design here, the caller may fall back to the legacy selector. */
export function requestDesignMountainMap(locate: string | null = null): boolean {
  if (typeof document === "undefined") return false;
  const detail: MountainMapRequest = { locate, handled: false };
  document.dispatchEvent(new CustomEvent<MountainMapRequest>(MOUNTAIN_MAP_EVENT, { detail }));
  return detail.handled;
}

/** The locate code a request carries, or null. A non-string / empty value is "no locate", never a guess. */
export function locateFromDetail(detail: MountainSelectRequest | null | undefined): string | null {
  const v = detail?.locate;
  return typeof v === "string" && v.trim() ? v.trim() : null;
}
